import React, { useState, useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { NavigationProvider, useNavigation } from './contexts/NavigationContext'
import { SiteProvider } from './contexts/SiteContext'
import { ContentProvider } from './contexts/ContentContext'
import { MediaProvider } from './contexts/MediaContext'
import ErrorBoundary from './components/ErrorBoundary'
import Header from './components/Header'
import Footer from './components/Footer'
import BurgerButton from './components/ui/BurgerButton'
import CtaButton from './components/ui/CtaButton'
import LanguageSwitcher from './components/LanguageSwitcher'
import LanguagePrompt from './components/ui/LanguagePrompt'
import { useSite } from './contexts/SiteContext'
import { useContent } from './contexts/ContentContext'
import HeroV3Section from './components/sections/HeroV3Section'
import ServicesSection from './components/sections/ServicesSection'
import WelcomeSection from './components/sections/WelcomeSection'
import StatementSection from './components/sections/StatementSection'
import QuoteSection from './components/sections/QuoteSection'
import WhatIsSection from './components/sections/WhatIsSection'
import ResearchersSection from './components/sections/ResearchersSection'
import ReferencesSection from './components/sections/ReferencesSection'
import FAQSection from './components/sections/FAQSection'
import ResearcherQuotesSection from './components/sections/ResearcherQuotesSection'
import PodcastVideoSection from './components/sections/PodcastVideoSection'
import PodcastSection from './components/sections/PodcastSection'
import ForWhomSection from './components/sections/ForWhomSection'
import AboutSection from './components/sections/AboutSection'
import ProcessSection from './components/sections/ProcessSection'
import CaseStudiesSection from './components/sections/CaseStudiesSection'
import TestimonialCarousel from './components/sections/TestimonialCarousel'
import TestimonialView from './components/sections/TestimonialView'
import { BookingSectionDark } from './components/sections/BookingSection'
import CtaImageSection from './components/sections/CtaImageSection'
import HeroImageSection from './components/sections/HeroImageSection'
import FullCTASection from './components/sections/FullCTASection'
import BodySection from './components/sections/BodySection'
import FinalCTASection from './components/sections/FinalCTASection'
import SectionDebugOverlay from './components/ui/SectionDebugOverlay'
import TranscriptPage from './pages/TranscriptPage'
import CaptureQuotePage from './pages/CaptureQuotePage'
import NotFound from './components/NotFound'
import { getSectionsOrder } from './config/sections.config'

function FloatingBurger() {
  const { sidecarOpen, setSidecarOpen, setIsBurgerClosing, isBurgerClosing, navigateTo, headerVisible } = useNavigation()
  const { menu } = useContent()
  const [ctaVisible, setCtaVisible] = useState(false)
  const [onDark, setOnDark] = useState(false)
  const [isIdle, setIsIdle] = useState(false)
  const containerRef = useRef(null)
  const idleTimerRef = useRef(null)

  useEffect(() => {
    const onScroll = () => setCtaVisible(window.scrollY > 1200)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Auto-hide on idle: slide up + fade after 2s of no activity
  useEffect(() => {
    const resetIdle = () => {
      setIsIdle(false)
      clearTimeout(idleTimerRef.current)
      if (!sidecarOpen) {
        idleTimerRef.current = setTimeout(() => setIsIdle(true), 2000)
      }
    }
    // Start the timer on mount
    if (!sidecarOpen) {
      idleTimerRef.current = setTimeout(() => setIsIdle(true), 2000)
    }
    window.addEventListener('scroll', resetIdle, { passive: true })
    window.addEventListener('touchstart', resetIdle, { passive: true })
    window.addEventListener('mousemove', resetIdle)
    return () => {
      clearTimeout(idleTimerRef.current)
      window.removeEventListener('scroll', resetIdle)
      window.removeEventListener('touchstart', resetIdle)
      window.removeEventListener('mousemove', resetIdle)
    }
  }, [sidecarOpen])

  // When sidecar opens, cancel idle state immediately
  useEffect(() => {
    if (sidecarOpen) {
      setIsIdle(false)
      clearTimeout(idleTimerRef.current)
    }
  }, [sidecarOpen])

  // Background-aware color switching: probe what's visible BEHIND the floating
  // overlay and switch to on-dark tokens when luminance is below threshold.
  //
  // Why walk the elementsFromPoint stack (top-down) until we find an opaque bg:
  // light sections (WelcomeSection, ServicesSection, etc.) do NOT set their own
  // background-color — the beige comes from the app-level wrapper div. So the
  // <section> element's computed backgroundColor is rgba(0,0,0,0). A naive RGB
  // parse treats that as black → luminance 0 → onDark stuck at true. Walking
  // the stack past transparent layers finds the first element that actually
  // paints color (the section itself if dark, the app wrapper if light).
  useEffect(() => {
    let ticking = false
    const parseRgb = (s) => {
      const m = s.match(/rgba?\(([^)]+)\)/)
      if (!m) return null
      const parts = m[1].split(',').map((v) => parseFloat(v.trim()))
      const [r, g, b] = parts
      const a = parts.length === 4 ? parts[3] : 1
      if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null
      return { r, g, b, a }
    }
    const detectBg = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        ticking = false
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return
        const x = rect.left + rect.width / 2
        const y = rect.top + rect.height / 2
        // elementsFromPoint returns a top-down stack of all elements at (x,y).
        const stack = document.elementsFromPoint(x, y)
        let found = null
        for (const el of stack) {
          if (containerRef.current?.contains(el)) continue
          const rgba = parseRgb(getComputedStyle(el).backgroundColor)
          if (rgba && rgba.a > 0.01) {
            found = rgba
            break
          }
        }
        if (!found) return
        const { r, g, b } = found
        // Perceived luminance; < 140 → treat as dark.
        setOnDark(0.299 * r + 0.587 * g + 0.114 * b < 140)
      })
    }
    detectBg()
    window.addEventListener('scroll', detectBg, { passive: true })
    window.addEventListener('resize', detectBg)
    return () => {
      window.removeEventListener('scroll', detectBg)
      window.removeEventListener('resize', detectBg)
    }
  }, [])

  const handleBurgerClick = () => {
    if (sidecarOpen) {
      setIsBurgerClosing(true)
      setTimeout(() => {
        setSidecarOpen(false)
        setIsBurgerClosing(false)
      }, 500)
    } else {
      setSidecarOpen(true)
    }
  }

  // Hide floating CTA + language while sidecar is open (they sit at z-100 over the drawer)
  const showCta = ctaVisible && !sidecarOpen && !isBurgerClosing
  const showLangSwitcher = !sidecarOpen && !isBurgerClosing

  return (
    <div
      ref={containerRef}
      className={`fixed top-2 right-8 z-[100] flex items-center gap-3 transition-all duration-[500ms] ${
        isIdle && !headerVisible ? '-translate-y-8 opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
      }`}
    >
      {/* CTA Button - appears on scroll */}
      <div
        className={`transition-opacity ${
          sidecarOpen && !isBurgerClosing ? 'duration-[100ms]' : 'delay-[200ms] duration-[800ms]'
        } ${showCta ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <CtaButton label={menu.header.cta.label} variant="primary" className="!py-2 !px-6 !text-xs" onDark={onDark} />
      </div>

      {/* Language Switcher - compact circular button */}
      <div
        className={`transition-opacity ${
          sidecarOpen && !isBurgerClosing ? 'duration-[100ms]' : 'delay-[200ms] duration-[800ms]'
        } ${showLangSwitcher ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <LanguageSwitcher compact={true} onDark={onDark} />
      </div>

      {/* Burger Button */}
      <BurgerButton isOpen={sidecarOpen} onClick={handleBurgerClick} onDark={onDark} />
    </div>
  )
}

function MainPage() {
  const { currentSite } = useSite()
  const { meta } = useContent()
  const sectionsOrder = getSectionsOrder(currentSite)

  useEffect(() => {
    if (meta?.title) document.title = meta.title
  }, [meta?.title])

  // Prevent browser from restoring scroll position on reload — always start at hero
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }
    window.scrollTo(0, 0)
  }, [currentSite])

  const sectionMap = {
    HeroV3Section: <HeroV3Section />,
    HeroImageSection: <HeroImageSection />,
    FullCTA_counterbalance: <FullCTASection contentKey="counterbalance" />,
    FullCTA_puzzle: <FullCTASection contentKey="puzzleBand" />,
    FullCTA_brick: <FullCTASection contentKey="brickBand" />,
    FullCTA_outcomesIntro: <FullCTASection contentKey="outcomesIntro" />,
    Body_mediaText: <BodySection contentKey="mediaText" />,
    Body_storyPartA: <BodySection contentKey="storyPartA" />,
    Body_storyPartB: <BodySection contentKey="storyPartB" />,
    Statement_freedom: <StatementSection contentKey="freedomStatement" />,
    Statement_storyQuote: <StatementSection contentKey="storyQuote" />,
    StatementSection: <StatementSection contentKey="statement" />,
    ServicesSection: <ServicesSection />,
    WelcomeSection: <WelcomeSection />,
    WhatIsSection: <WhatIsSection />,
    ResearchersSection: <ResearchersSection />,
    ResearcherQuotesSection: <ResearcherQuotesSection />,
    PodcastVideoSection: <PodcastVideoSection />,
    QuoteSection: <QuoteSection />,
    ForWhomSection: <ForWhomSection contentKey="forWhom" />,
    ForWhom_outcomesList: <ForWhomSection contentKey="outcomesList" />,
    AboutSection: <AboutSection />,
    ProcessSection: <ProcessSection contentKey="processSection" />,
    PodcastSection: <PodcastSection />,
    CaseStudiesSection: <CaseStudiesSection />,
    TestimonialCarousel: <TestimonialView />,
    BookingSection: <BookingSectionDark />,
    FAQSection: <FAQSection />,
    ReferencesSection: <ReferencesSection />,
    CtaImageSection: <CtaImageSection />,
    FinalCTASection: <FinalCTASection />,
  }

  return (
    <div
      data-site={currentSite}
      className="bg-color-bg-light bg-paper min-h-screen font-primary text-color-text"
    >
      <FloatingBurger />
      <LanguagePrompt />
      <Header />
      <main>
        {sectionsOrder.map((sectionName) => (
          <React.Fragment key={sectionName}>
            {sectionMap[sectionName] || null}
          </React.Fragment>
        ))}
      </main>
      <Footer />
      <SectionDebugOverlay />
    </div>
  )
}

function SitePage() {
  // Key forces full remount of providers + ErrorBoundary when site/lang changes
  // so a prior crash cannot stick after menu site-switch.
  const { site, lang } = useParams()
  const resetKey = `${site || 'regression'}/${lang || 'de'}`

  return (
    <ErrorBoundary key={resetKey} resetKey={resetKey}>
      <MediaProvider>
        <SiteProvider>
          <ContentProvider>
            <MainPage />
          </ContentProvider>
        </SiteProvider>
      </MediaProvider>
    </ErrorBoundary>
  )
}

export default function App() {
  return (
    <NavigationProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/:site/:lang" element={<SitePage />} />
          <Route path="/" element={<Navigate to="/regression/de" replace />} />
          <Route path="/:site" element={<Navigate to="/regression/de" replace />} />
          <Route path="/transkript" element={<TranscriptPage />} />
          <Route path="/capture/quote" element={<CaptureQuotePage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </NavigationProvider>
  )
}
